import type { ComponentProps, ReactNode } from 'react';

import { NativeSelect } from '@/components/ui/native-select';

import { useQuickCreate } from './use-quick-create';
import type { PickerEntity } from './roster';

/**
 * GEN-51 FOR A PICKER THAT IS A NATIVE `<select>`.
 *
 * Not every "Pick from list" field in this product is the `<Typeahead>`.
 * Categories and units — two of D26's four simple records — are picked from
 * native selects, and GEN-51 governs those fields exactly as it governs the
 * others: *"a '＋ Create new' choice at the bottom of its dropdown."* A
 * `<select>` has a dropdown and can have a choice at the bottom of it, so the
 * rule is satisfiable here without converting the control.
 *
 * Converting them to typeaheads was considered and refused: it is a far larger
 * change than D26 asks for, and it would fork picker behaviour mid-wave —
 * some category fields searching, others not, depending on which screens a
 * wave reached.
 *
 * ── THE SENTINEL IS AN ACT, AND MUST NEVER BECOME THE VALUE ───────────────
 *
 * The guarantee is real and is asserted in `d26-quick-create-select.test.tsx`:
 * choosing the create row leaves the control showing the unit it showed, and
 * `onChange` never sees the sentinel, so nothing downstream — a form store, a
 * PATCH-on-change field like the BOM's — can ever have been handed it. The
 * field's value moves once, later, and only for a record that exists
 * (`onCreated`), which is the same promise `<Typeahead>`'s create row makes.
 *
 * WHAT ACTUALLY DELIVERS THAT, MEASURED RATHER THAN ASSUMED. It is the
 * CONTROLLED-COMPONENT contract, not the assignment below. Choosing the
 * sentinel opens the window, which is a state change, which re-renders, which
 * puts the DOM value back to the `value` prop. I first wrote the opposite here
 * — "if the state does not change there may be no re-render" — and then
 * removed the assignment and ran the file: **6 passed, including the assertion
 * that the value snaps back.** There is no reachable path through this
 * component where choosing the sentinel does not change state, because
 * choosing it is what opens the window.
 *
 * SO WHY IS THE LINE STILL HERE. For the gap between the change event and the
 * commit, which jsdom does not have and a browser does: under concurrent
 * rendering the select can paint "＋ Create new" for a frame before React puts
 * it back. The assignment closes that frame. **No test in this suite can tell
 * its presence from its absence** — that is measured, above, not suspected —
 * so it is documented as what it is rather than defended as load-bearing. A
 * later reader who deletes it will see nothing go red, and should know from
 * this paragraph that the deletion is a judgement about a visual frame, not
 * about correctness.
 *
 * ── AND WHY THE SENTINEL CANNOT COLLIDE ───────────────────────────────────
 *
 * Every real value in these selects is a UUID or the empty string. The
 * sentinel is neither, and it is a module constant rather than a literal
 * typed at two sites, so the option that carries it and the branch that tests
 * for it cannot drift apart.
 */
const CREATE_SENTINEL = '__quick_create__';

export function QuickCreateSelect({
  entity,
  value,
  onChange,
  onCreated,
  children,
  ...rest
}: {
  entity: PickerEntity;
  /** The id the field holds, or '' for none. */
  value: string;
  onChange: (value: string) => void;
  /**
   * The new record's id. Separate from `onChange` because a screen usually has
   * more to do when a record is BORN than when one is merely picked — the item
   * form pulls a category's default unit across, for instance — and collapsing
   * the two would make that impossible to express.
   */
  onCreated?: ((id: string) => void) | undefined;
  /** The real options. The create row is appended after them. */
  children: ReactNode;
} & Omit<ComponentProps<'select'>, 'value' | 'onChange' | 'children'>) {
  const quick = useQuickCreate(entity, (id) => {
    onChange(id);
    onCreated?.(id);
  });

  return (
    <>
      <NativeSelect
        {...rest}
        value={value}
        onChange={(event) => {
          if (event.target.value === CREATE_SENTINEL) {
            // The `return` is the load-bearing half: it is what keeps the
            // sentinel out of `onChange`, and removing it turns this file's
            // suite red. The assignment beside it closes a one-frame repaint
            // that only a real browser has — see the block above, which says
            // plainly that no test here can distinguish it.
            event.target.value = value;
            quick.createNew?.onSelect();
            return;
          }
          onChange(event.target.value);
        }}
      >
        {children}
        {/*
         * GEN-51's choice, last, and absent when the role may not create —
         * `createNew` is `undefined` in that case, which is the rule's own
         * "if the user's role allows creating that record".
         */}
        {quick.createNew === undefined ? null : (
          <option value={CREATE_SENTINEL}>{quick.createNew.label}</option>
        )}
      </NativeSelect>
      {quick.dialog}
    </>
  );
}
